import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  MenuItem,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Stack,
  Checkbox,
  ListItemText
} from "@mui/material";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { can } from "../../../auth/can";
import { useAuth } from "../../../auth/useAuth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = APP_API_V1_BASE;

export default function CreateDeployment({ open, onClose, onSuccess }) {
  const token = localStorage.getItem("authToken");
  const { permissions, activeOrg } = useAuth();

  const isPlatformAdmin = can(permissions, "system.manage");

  const [deviceTypes, setDeviceTypes] = useState([]);
  const [data, setData] = useState(null);
  const [deviceTypeId, setDeviceTypeId] = useState("");
  const [firmwareId, setFirmwareId] = useState("");
  const [targetIds, setTargetIds] = useState([]);
  const [rollout, setRollout] = useState("IMMEDIATE");
  const [scheduledAt, setScheduledAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tempTargetIds, setTempTargetIds] = useState([]);

  // Controls whether device dropdown is open
  const [deviceMenuOpen, setDeviceMenuOpen] = useState(false);

  const availableDevices = useMemo(
    () => (Array.isArray(data?.devices) ? data.devices : []),
    [data],
  );
  const availableFirmwares = useMemo(
    () =>
      (Array.isArray(data?.firmware_packages)
        ? data.firmware_packages
        : []
      ).filter((firmware) => firmware?.is_active !== false),
    [data],
  );
  const minScheduledTime = useMemo(() => dayjs().add(5, "minute"), [open]);
  const scheduledDateValue = useMemo(
    () => (scheduledAt ? dayjs(scheduledAt) : null),
    [scheduledAt],
  );
  const isScheduledAtInvalid = useMemo(() => {
    if (rollout !== "SCHEDULED" || !scheduledDateValue) return false;
    return !scheduledDateValue.isValid() || scheduledDateValue.isBefore(minScheduledTime);
  }, [rollout, scheduledDateValue, minScheduledTime]);

  const resetForm = () => {
    setDeviceTypeId("");
    setFirmwareId("");
    setTargetIds([]);
    setRollout("IMMEDIATE");
    setScheduledAt("");
    setData(null);
    setSubmitting(false);
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  useEffect(() => {
    if (!open || !token) return;

    axios
      .get(`${API_BASE}/firmware/packages`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((response) => {
        const packages = Array.isArray(response.data) ? response.data : [];
        const deduped = [];
        const seen = new Set();

        packages.forEach((pkg) => {
          const id = pkg?.device_type?.id || pkg?.device_type_id;
          const name = pkg?.device_type?.name;
          if (!id || !name || seen.has(id)) return;
          seen.add(id);
          deduped.push({ id, name });
        });

        setDeviceTypes(deduped);
        setError("");
      })
      .catch((err) => {
        console.error("Firmware package fetch failed", err);
        setDeviceTypes([]);
        setError(
          err?.response?.data?.error ||
          "Unable to load firmware-capable device types for the active organization.",
        );
      });
  }, [open, token, activeOrg?.id]);

  useEffect(() => {
    if (!deviceTypeId || !token) {
      setData(null);
      setFirmwareId("");
      setTargetIds([]);
      return;
    }

    axios
      .get(
        `${API_BASE}/firmware/packages/device-types/${deviceTypeId}/artifacts`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      )
      .then((response) => {
        const payload = response.data ?? null;

        setData(payload);
        setTargetIds([]); // Nothing selected by default
        setError("");
      })
      .catch((err) => {
        console.error("Firmware fetch failed", err);
        setData(null);
        setError(
          err?.response?.data?.error ||
          "Unable to load firmware artifacts and target devices for the selected device family.",
        );
      });
  }, [deviceTypeId, token, activeOrg?.id]);

  const submit = async () => {
    if (!firmwareId || !deviceTypeId || targetIds.length === 0) return;

    try {
      setSubmitting(true);
      setError("");

      const response = await axios.post(
        `${API_BASE}/firmware/deployments`,
        {
          firmware_id: firmwareId,
          target_type: "DEVICE",
          target_ids: targetIds,
          rollout_strategy: rollout,
          scheduled_at:
            rollout === "SCHEDULED" && scheduledAt
              ? new Date(scheduledAt).toISOString()
              : null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            "Content-Type": "application/json",
          },
        },
      );

      await Promise.resolve(onSuccess?.(response.data?.deployments || []));
      handleClose();
    } catch (err) {
      console.error("Deployment failed", err);
      setError(
        err?.response?.data?.error ||
        "Deployment could not be created. Please review the selected firmware and target devices.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isPlatformAdmin || !can(permissions, "firmware.deploy")) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "14px",
          boxShadow: "0 22px 70px rgba(11, 28, 58, 0.35)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex", // 👈 important
          alignItems: "center",
          justifyContent: "space-between", // 👈 pushes button to right
          fontWeight: 800,
          fontSize: 17,
          px: 4,
          pt: 3,
          pb: 1.5,
          color: "#fff",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
        }}
      >
        Create Firmware Deployment
        <IconButton onClick={onClose} size="small" sx={{ color: "#e7e7e7" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 2.5, pt: 1.5 }}>
        <Paper sx={{ pt: 1, boxShadow: "none" }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Platform administrators control firmware rollouts centrally.
            Immediate rollouts publish an <strong>OTA_Update</strong> command
            over MQTT to each selected device, and simulator acknowledgements
            then drive the live progress states. If a device stays offline and
            never sends the first acknowledgement, the pending deployment will
            automatically time out after 5 minutes so you can retry.
          </Alert>

          {rollout === "SCHEDULED" && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Scheduled rollouts are dispatched by the backend scheduler when
              the scheduled time is reached. During testing, allow a short
              polling lag before the OTA command appears on MQTT.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            select
            label="Device Type"
            fullWidth
            margin="dense"
            value={deviceTypeId}
            onChange={(e) => setDeviceTypeId(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          >
            <MenuItem value="" />
            {deviceTypes.map((deviceType) => (
              <MenuItem key={deviceType.id} value={deviceType.id}>
                {deviceType.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Firmware"
            fullWidth
            margin="dense"
            value={firmwareId}
            onChange={(e) => setFirmwareId(e.target.value)}
            disabled={!availableFirmwares.length}
          >
            <MenuItem value="" />
            {availableFirmwares.map((firmware) => (
              <MenuItem key={firmware.id} value={firmware.id}>
                {firmware.name} - v{firmware.version}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Select Devices"
            fullWidth
            margin="dense"
            disabled={!availableDevices.length}
            value={targetIds}
            onChange={() => { }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
              },
            }}
            SelectProps={{
              multiple: true,

              open: deviceMenuOpen,

              onOpen: () => {
                // Copy currently selected devices into temporary selection
                setTempTargetIds([...targetIds]);
                setDeviceMenuOpen(true);
              },

              onClose: () => {
                setDeviceMenuOpen(false);
              },

              renderValue: (selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <span style={{ color: "#999" }}>
                      Select Devices
                    </span>
                  );
                }

                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                    }}
                  >
                    {selected.map((id) => {
                      const device = availableDevices.find(
                        (item) => String(item.id) === String(id)
                      );

                      return (
                        <Chip
                          key={id}
                          label={`${device?.name || "Device"} (${id})`}
                          size="small"
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          onDelete={() => {
                            setTargetIds((prev) =>
                              prev.filter(
                                (item) => String(item) !== String(id)
                              )
                            );
                          }}
                        />
                      );
                    })}
                  </Box>
                );
              },

              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    borderRadius: "10px",
                  },
                },
              },
            }}
          >
            {availableDevices.map((device) => {
              const deviceId = String(device.id);

              const isSelected = tempTargetIds.some(
                (id) => String(id) === deviceId
              );

              return (
                <MenuItem
                  key={device.id}
                  value={deviceId}
                  onClick={(event) => {
                    event.preventDefault();

                    setTempTargetIds((prev) => {
                      if (
                        prev.some(
                          (id) => String(id) === deviceId
                        )
                      ) {
                        return prev.filter(
                          (id) => String(id) !== deviceId
                        );
                      }

                      return [...prev, deviceId];
                    });
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      p: 0.5,
                      mr: 1,
                    }}
                  />

                  <ListItemText
                    primary={device.name}
                    secondary={`ID: ${device.id}`}
                  />
                </MenuItem>
              );
            })}

            {/* OK BUTTON */}
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                borderTop: "1px solid #ddd",
                p: 1,
                display: "flex",
                justifyContent: "flex-end",
                zIndex: 10,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();

                  setTargetIds([...tempTargetIds]);
                  setDeviceMenuOpen(false);
                }}
                sx={{
                  borderRadius: "8px",
                  minWidth: "80px",
                }}
              >
                OK
              </Button>
            </Box>
          </TextField>

          <Typography mt={2}>Rollout Strategy</Typography>
          <RadioGroup
            row
            value={rollout}
            onChange={(e) => setRollout(e.target.value)}
            sx={{ mt: 0.8 }}
          >
            <FormControlLabel
              value="IMMEDIATE"
              control={<Radio />}
              label="Immediate"
            />
            <FormControlLabel
              value="SCHEDULED"
              control={<Radio />}
              label="Scheduled"
            />
          </RadioGroup>

          {rollout === "SCHEDULED" && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Schedule Deployment"
                value={scheduledDateValue}
                onChange={(newValue) => {
                  if (!newValue) {
                    setScheduledAt("");
                    return;
                  }

                  if (!newValue.isValid()) {
                    setScheduledAt("");
                    return;
                  }

                  setScheduledAt(newValue.toISOString());
                }}
                minDateTime={minScheduledTime}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "dense",
                    helperText:
                      "Deployment must be scheduled at least 5 minutes in the future.",
                    error: isScheduledAtInvalid,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          )}

          <Box
            mt={3}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "stretch",
              gap: 2,
            }}
          >
            {/* Deploy Button */}
            <Button
              variant="contained"
              onClick={submit}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              disabled={
                submitting ||
                !firmwareId ||
                !deviceTypeId ||
                targetIds.length === 0 ||
                (rollout === "SCHEDULED" && (!scheduledAt || isScheduledAtInvalid))
              }
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                borderRadius: "10px",
                textTransform: "uppercase",
                fontWeight: 800,
                fontSize: 14,
                color: "#fff !important",
                background: "linear-gradient(135deg, #225387ff, #2A67D2)",
                boxShadow: "0 8px 18px rgba(28, 109, 214, 0.28)",
                "&:hover": {
                  background: "linear-gradient(135deg, #166BC4, #245CB6)",
                },
              }}
            >
              {submitting ? "Deploying..." : "Deploy Firmware"}
            </Button>

            {/* Cancel Button */}
            <Button
              variant="contained"
              onClick={handleClose}
              disabled={submitting}
              sx={{
                minWidth: { xs: "100%", sm: "auto" },
                borderRadius: "10px",
                fontWeight: 700,
                background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                boxShadow: "0 8px 20px rgba(245,158,11,0.25)",
                color: "#000",
                "&:hover": {
                  background: "linear-gradient(135deg, #FCD34D, #D97706)",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
}
